import React, { useEffect, useState } from "react";
import { Zap, Star, Trophy, Award } from "lucide-react";
import { cn } from "@/lib/utils";

interface Position {
  x: number;
  y: number;
}

interface PointsPopupProps {
  points: number;
  reason?: string;
  position: Position;
  onComplete?: () => void;
  type?: "points" | "xp" | "achievement" | "bonus";
}

const typeConfig = {
  points: { icon: Zap, color: "text-warning", bg: "bg-warning/20", border: "border-warning" },
  xp: { icon: Star, color: "text-primary", bg: "bg-primary/20", border: "border-primary" },
  achievement: { icon: Trophy, color: "text-success", bg: "bg-success/20", border: "border-success" },
  bonus: { icon: Award, color: "text-info", bg: "bg-info/20", border: "border-info" },
};

export const PointsPopup: React.FC<PointsPopupProps> = ({
  points,
  reason,
  position,
  onComplete,
  type = "points",
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const config = typeConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onComplete?.(), 300);
    }, 2000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed z-50 pointer-events-none",
        "animate-fade-in"
      )}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: "translate(-50%, -50%)",
        animation: "fade-in 0.3s ease-out, scale-in 0.3s ease-out, float-up 2s ease-out",
      }}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-full border-2 shadow-lg backdrop-blur-sm",
          config.bg,
          config.border,
          "animate-scale-in"
        )}
      >
        <Icon className={cn("w-5 h-5", config.color)} />
        <div className="flex flex-col">
          <span className={cn("text-lg font-bold", config.color)}>
            +{points}
          </span>
          {reason && (
            <span className="text-xs text-foreground/80 whitespace-nowrap">
              {reason}
            </span>
          )}
        </div>
      </div>

      {/* Particle Effects */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={cn(
              "absolute w-1.5 h-1.5 rounded-full",
              config.color.replace("text-", "bg-")
            )}
            style={{
              left: "50%",
              top: "50%",
              animation: `particle-burst 1s ease-out ${i * 0.1}s forwards`,
              transform: `rotate(${i * 60}deg) translateY(-20px)`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float-up {
          0% {
            transform: translate(-50%, -50%);
            opacity: 1;
          }
          70% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -120px);
            opacity: 0;
          }
        }

        @keyframes particle-burst {
          0% {
            transform: rotate(var(--rotation, 0deg)) translateY(0);
            opacity: 1;
          }
          100% {
            transform: rotate(var(--rotation, 0deg)) translateY(-40px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

// Helper hook to manage multiple popups
export const usePointsPopup = () => {
  const [popups, setPopups] = useState<Array<{
    id: string;
    points: number;
    reason?: string;
    position: Position;
    type?: "points" | "xp" | "achievement" | "bonus";
  }>>([]);

  const showPopup = (
    points: number,
    position: Position,
    reason?: string,
    type: "points" | "xp" | "achievement" | "bonus" = "points"
  ) => {
    const id = `popup-${Date.now()}-${Math.random()}`;
    setPopups((prev) => [...prev, { id, points, reason, position, type }]);
  };

  const removePopup = (id: string) => {
    setPopups((prev) => prev.filter((popup) => popup.id !== id));
  };

  const PopupContainer = () => (
    <>
      {popups.map((popup) => (
        <PointsPopup
          key={popup.id}
          points={popup.points}
          reason={popup.reason}
          position={popup.position}
          type={popup.type}
          onComplete={() => removePopup(popup.id)}
        />
      ))}
    </>
  );

  return { showPopup, PopupContainer };
};
